import React, { useState } from 'react';
import './VoiceAnalysisPanel.css';
import { VoiceQcAnalysis, ACE_CATEGORY_NAMES, ACE_CATEGORY_DESCRIPTIONS, LANGUAGE_NAMES } from './types';
import { ScoreRing } from '../../components/ScoreRing/ScoreRing';
import { MetricCard } from '../../components/MetricCard/MetricCard';
import { Tag } from '../../components/Tag/Tag';

interface QCStandard {
  id: number;
  name: string;
  source_file: string | null;
  sheet_name: string | null;
  description: string | null;
  dimensions: any[] | null;
  is_active: boolean;
  created_at: string | null;
}

type AnalysisState = {
  transcription?: string;
  detectedLanguage?: string;
  analysis?: VoiceQcAnalysis;
  status: 'idle' | 'transcribing' | 'transcribed' | 'analyzing' | 'completed' | 'error';
  error?: string;
};

type VoiceAnalysisPanelProps = {
  analysisState: AnalysisState;
  loading: boolean;
  selectedStandard?: QCStandard | null;
};

export const VoiceAnalysisPanel: React.FC<VoiceAnalysisPanelProps> = ({
  analysisState,
  selectedStandard,
}) => {
  const { status, transcription, detectedLanguage, analysis, error } = analysisState;
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // 显示语音识别中
  if (status === 'transcribing') {
    return (
      <div className="analysis-panel">
        <div className="analysis-loading-header">
          <div className="analysis-loading-spinner" />
          <div className="analysis-loading-title">正在识别语音...</div>
          <div className="analysis-loading-desc">使用千问语音识别模型转换音频为文字</div>
        </div>
      </div>
    );
  }

  // 显示识别结果和质检分析中
  if (status === 'transcribed' || status === 'analyzing') {
    return (
      <div className="analysis-panel">
        <div className="analysis-section">
          <div className="analysis-section-header">
            <div className="analysis-section-title">
              ✅ 语音识别完成
              {detectedLanguage && detectedLanguage !== 'zh' && (
                <span className="language-badge">{LANGUAGE_NAMES[detectedLanguage] || detectedLanguage}</span>
              )}
            </div>
          </div>
          <div className="analysis-section-content">
            <div className="transcription-box">{transcription}</div>
          </div>
        </div>
        {status === 'analyzing' && (
          <div className="analysis-loading-section">
            <div className="analysis-loading-spinner" />
            <div className="analysis-loading-title">ACE质检分析中...</div>
            <div className="analysis-loading-desc">正在基于ACE CX QA Culture Rubric进行多维度质检分析</div>
          </div>
        )}
      </div>
    );
  }

  // 显示错误
  if (status === 'error') {
    return (
      <div className="analysis-panel analysis-error">
        <div className="analysis-error-icon">❌</div>
        <div className="analysis-error-title">分析失败</div>
        <div className="analysis-error-message">{error}</div>
      </div>
    );
  }

  // 等待上传
  if (status === 'idle' || !analysis) {
    // 如果选择了自定义质检标准，显示其评估维度
    if (selectedStandard && selectedStandard.dimensions && selectedStandard.dimensions.length > 0) {
      return (
        <div className="analysis-panel analysis-empty">
          <div className="analysis-empty-title">等待分析音频...</div>
          <div className="analysis-empty-desc">
            上传通话录音，使用【{selectedStandard.name}】标准进行智能质检评估。
          </div>
          <div className="analysis-empty-standard">
            <div className="analysis-empty-standard-title">评估维度：</div>
            <div className="analysis-empty-dimensions">
              {selectedStandard.dimensions.map((dim: any, index: number) => (
                <div key={index} className="analysis-empty-dimension">
                  <div className="dimension-header">
                    <span className="dimension-name">• <strong>{dim.name}</strong></span>
                    {dim.weight && <span className="dimension-weight">权重 {dim.weight}%</span>}
                    {dim.is_fatal && <span className="dimension-fatal">致命项</span>}
                  </div>
                  {dim.criteria && dim.criteria.length > 0 && (
                    <div className="dimension-criteria">
                      {dim.criteria.slice(0, 5).map((c: any, i: number) => (
                        <span key={i} className="criterion-tag">
                          {c.name}
                          {c.is_fatal && <span className="fatal-mark">!</span>}
                        </span>
                      ))}
                      {dim.criteria.length > 5 && (
                        <span className="criterion-more">+{dim.criteria.length - 5}项</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // 默认ACE标准
    return (
      <div className="analysis-panel analysis-empty">
        <div className="analysis-empty-title">等待分析音频...</div>
        <div className="analysis-empty-desc">
          上传通话录音，基于ACE CX QA Culture Rubric评价体系，从准确性(Accurate)、个性化(Customized)、高效性(Efficient)三大维度进行智能评估。
        </div>
        <div className="analysis-empty-points">
          <span>• <strong>Accurate</strong> - 信息准确性、流程合规性（7项指标）</span>
          <span>• <strong>Customized</strong> - 服务个性化、客户关怀（6项指标）</span>
          <span>• <strong>Efficient</strong> - 服务效率、问题解决速度（6项指标）</span>
          <span>• <strong>ZTP</strong> - 零容忍政策监控</span>
        </div>
      </div>
    );
  }

  const {
    overallScore,
    accurateScore,
    customizedScore,
    efficientScore,
    aceScores,
    ztpViolation,
    ztpDetails,
    detectedLanguage: analysisLang,
    politePhrases,
    missingPolitePhrases,
    rudePhrases,
    summary,
    suggestions,
    sentiment,
    dialogueFlow,
    timeline,
  } = analysis;

  const tone = ztpViolation ? 'bad' : overallScore >= 85 ? 'good' : overallScore >= 70 ? 'warn' : 'bad';

  const handleDownloadTranscription = () => {
    if (!analysis.transcription) {
      alert('转录文本不可用');
      return;
    }
    const content = `语音转录文本\n识别时间: ${new Date().toLocaleString('zh-CN')}\n检测语言: ${LANGUAGE_NAMES[analysisLang] || analysisLang}\n文本长度: ${analysis.transcription.length} 字符\n${'='.repeat(60)}\n\n${analysis.transcription}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transcription_${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  return (
    <div className="analysis-panel">
      {/* ZTP警告 */}
      {ztpViolation && (
        <div className="ztp-warning">
          <div className="ztp-warning-icon">⚠️</div>
          <div className="ztp-warning-content">
            <div className="ztp-warning-title">零容忍政策(ZTP)违规</div>
            <div className="ztp-warning-details">{ztpDetails || '检测到严重违规行为，评分已归零'}</div>
          </div>
        </div>
      )}

      {/* 头部评分 */}
      <div className="analysis-header">
        <div className="analysis-title-wrap">
          <div className="analysis-label-row">
            <Tag type={tone}>
              {ztpViolation ? 'ZTP违规' : tone === 'good' ? '优秀' : tone === 'warn' ? '良好' : '需改进'}
            </Tag>
            <Tag type="neutral">
              情绪：{sentiment === 'positive' ? '积极' : sentiment === 'neutral' ? '中性' : '消极'}
            </Tag>
            {analysisLang && analysisLang !== 'zh' && (
              <Tag type="neutral">语言：{LANGUAGE_NAMES[analysisLang] || analysisLang}</Tag>
            )}
            {analysis.transcription && (
              <button className="analysis-download-btn" onClick={handleDownloadTranscription} title="下载转录文本">
                📥 下载转录
              </button>
            )}
          </div>
          <div className="analysis-title">
            {selectedStandard ? `【${selectedStandard.name}】质检结果` : 'ACE智能语音质检结果'}
          </div>
          <div className="analysis-subtitle">{summary}</div>
        </div>
        <div className="analysis-score-ring">
          <ScoreRing score={overallScore} size={84} tone={tone} label="综合得分" />
        </div>
      </div>

      {/* 自定义标准维度评分 或 ACE三大维度评分卡片 */}
      {selectedStandard && selectedStandard.dimensions && selectedStandard.dimensions.length > 0 ? (
        // 自定义标准：显示其维度
        <div className="analysis-metrics-grid custom-metrics">
          {aceScores && aceScores.length > 0 ? (
            // 如果有解析出的维度评分，显示它们
            aceScores.slice(0, 4).map((cat, index) => (
              <MetricCard
                key={index}
                title={cat.category}
                score={cat.score}
                description={cat.criteria?.[0]?.comment || ''}
                highlight={`${cat.criteria?.length || 0}项评估指标`}
              />
            ))
          ) : (
            // 否则显示标准定义的维度
            selectedStandard.dimensions.slice(0, 4).map((dim: any, index: number) => (
              <MetricCard
                key={index}
                title={dim.name}
                score={index === 0 ? accurateScore ?? overallScore : index === 1 ? customizedScore ?? overallScore : efficientScore ?? overallScore}
                description={dim.criteria?.[0]?.description || ''}
                highlight={`${dim.criteria?.length || 0}项评估指标`}
              />
            ))
          )}
        </div>
      ) : (
        // ACE标准：显示三大维度
        <div className="analysis-metrics-grid ace-metrics">
          <MetricCard
            title="Accurate · 准确性"
            score={accurateScore ?? 0}
            description={ACE_CATEGORY_DESCRIPTIONS['Accurate']}
            highlight={`包含${aceScores?.find(s => s.category === 'Accurate')?.criteria.length || 7}项评估指标`}
          />
          <MetricCard
            title="Customized · 个性化"
            score={customizedScore ?? 0}
            description={ACE_CATEGORY_DESCRIPTIONS['Customized']}
            highlight={`包含${aceScores?.find(s => s.category === 'Customized')?.criteria.length || 6}项评估指标`}
          />
          <MetricCard
            title="Efficient · 高效性"
            score={efficientScore ?? 0}
            description={ACE_CATEGORY_DESCRIPTIONS['Efficient']}
            highlight={`包含${aceScores?.find(s => s.category === 'Efficient')?.criteria.length || 6}项评估指标`}
          />
        </div>
      )}

      {/* 详细评分展开 */}
      {aceScores && aceScores.length > 0 && (
        <div className="analysis-section ace-details-section">
          <div className="analysis-section-header">
            <div className="analysis-section-title">
              {selectedStandard ? `${selectedStandard.name} 详细评分` : 'ACE详细评分指标（共19项）'}
            </div>
          </div>
          <div className="analysis-section-content">
            {aceScores.map((category) => (
              <div key={category.category} className="ace-category-block">
                <div 
                  className={`ace-category-header ${expandedCategory === category.category ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(category.category)}
                >
                  <span className="ace-category-name">
                    {selectedStandard ? category.category : (ACE_CATEGORY_NAMES[category.category] || category.category)}
                  </span>
                  <span className="ace-category-score">{category.score}分</span>
                  <span className="ace-category-toggle">{expandedCategory === category.category ? '▼' : '▶'}</span>
                </div>
                {expandedCategory === category.category && (
                  <div className="ace-criteria-list">
                    {category.criteria.map((criterion) => (
                      <div key={criterion.id} className={`ace-criterion-item ${criterion.passed ? 'passed' : 'failed'}`}>
                        <span className="ace-criterion-status">{criterion.passed ? '✓' : '✗'}</span>
                        <span className="ace-criterion-name">{criterion.name}</span>
                        <span className="ace-criterion-score">{criterion.score}分</span>
                        <span className="ace-criterion-comment">{criterion.comment}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 对话时间线（含翻译） */}
      {timeline && timeline.length > 0 && (
        <div className="analysis-section">
          <div className="analysis-section-header">
            <div className="analysis-section-title">对话时间线</div>
          </div>
          <div className="analysis-section-content">
            <div className="timeline-list">
              {timeline.map((u, i) => (
                <div key={i} className={`timeline-item timeline-${u.speaker}`}>
                  <div className="timeline-header">
                    <span className="timeline-speaker">{u.speaker === 'agent' ? '🎧 坐席' : '👤 客户'}</span>
                    {u.language && u.language !== 'zh' && (
                      <span className="timeline-lang">{LANGUAGE_NAMES[u.language] || u.language}</span>
                    )}
                  </div>
                  <div className="timeline-text">{u.text}</div>
                  {u.textCn && u.language !== 'zh' && (
                    <div className="timeline-translation">
                      <span className="translation-label">中文：</span>
                      {u.textCn}
                    </div>
                  )}
                  {(u.politePhrases?.length > 0 || u.rudePhrases?.length > 0) && (
                    <div className="timeline-tags">
                      {u.politePhrases?.map((p, j) => (
                        <span key={`p-${j}`} className="tag-positive">{p}</span>
                      ))}
                      {u.rudePhrases?.map((r, j) => (
                        <span key={`r-${j}`} className="tag-negative">{r}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 礼貌用语与关键话术 */}
      <div className="analysis-section">
        <div className="analysis-section-header">
          <div className="analysis-section-title">礼貌用语与关键话术</div>
        </div>
        <div className="analysis-section-content">
          <div className="analysis-pills">
            <div className="analysis-pills-column">
              <div className="analysis-label">已使用的礼貌用语：</div>
              <div className="analysis-tagline">
                {politePhrases.length ? politePhrases.join('、') : '暂无明显标准礼貌用语'}
              </div>
            </div>
            <div className="analysis-pills-column">
              <div className="analysis-label">建议补充的话术：</div>
              <div className="analysis-tagline">
                {missingPolitePhrases.length ? missingPolitePhrases.join('、') : '关键礼貌话术较为完整'}
              </div>
            </div>
            {rudePhrases.length > 0 && (
              <div className="analysis-pills-column">
                <div className="analysis-label risk">风险·敏感词：</div>
                <div className="analysis-tagline risk">{rudePhrases.join('、')}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 对话流程 */}
      <div className="analysis-section">
        <div className="analysis-section-header">
          <div className="analysis-section-title">对话流程</div>
        </div>
        <div className="analysis-section-content">
          <div className="dialogue-flow-text">{dialogueFlow}</div>
        </div>
      </div>

      {/* 优化建议 */}
      {suggestions.length > 0 && (
        <div className="analysis-section">
          <div className="analysis-section-header">
            <div className="analysis-section-title">
              {selectedStandard ? `优化建议（基于${selectedStandard.name}）` : '优化建议（基于ACE评价体系）'}
            </div>
          </div>
          <div className="analysis-section-content">
            <ul className="analysis-suggestions">
              {suggestions.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
